import { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDownUp, BarChart3, Building2, ChevronDown, CircleDot, Contact, Download, Filter, Handshake, KanbanSquare, List, ListTodo, MoreHorizontal, Pencil, Plus, RefreshCw, Settings, UserRound, X } from 'lucide-react';
import { Botao, classesBotao } from '../../componentes/base';
import { Selecao } from '../../componentes/formulario';
import { Menu } from '../../componentes/menu';
import { Drawer } from '../../componentes/sobreposicao';
import { ICONE_FEEDBACK, useFeedback } from '../../componentes/feedback';
import { nomeDaEmpresa, useEmpresasDoCatalogo } from '../../componentes/verticais';
import { DialogoCriacao, useAbrirCriacao, useContextoDaCriacao, useCriacaoDaChave } from '../../nucleo/criacao-rapida';
import { useToast } from '../../componentes/toast';
import { api, get, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { rotuloEtapaPipeline, rotuloFunilPipeline } from '../../nucleo/rotulos';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { DrawerFiltros } from './ComercialFiltros';
import { FunilComercial, type EstatisticaEtapa, type EtapaCrm, type NegociacaoCrm } from './ComercialFunil';
import { ListaComercial } from './ComercialLista';
import { baixarTexto, FiltroMultiplo } from './CrmComponentes';
import { csv } from './crm-sugestoes';
import { dataHora, dinheiro, nomePessoa } from '../../nucleo/formato';
import { contarFiltros, filtrosVazios, ORDENS, paramsDosFiltros, STATUS, type FiltrosComercial, type Ordem } from './crm-filtros';
import { CAMINHO_CONFIGURACOES, caminhoDaNegociacao, funilDoPedido, lembrarFunil, lerFunilLembrado, lerPedidoDoRelatorio } from './crm-caminhos';
import { CriarNegociacaoCrm } from './CriarNegociacaoCrm';
import { SobreNegociacao } from './SobreNegociacao';
import { BotaoAtalhoTarefas } from './AtalhoTarefas';
import { FichaNegociacao } from './FichaNegociacao';
import { NovaProposta } from './FichaPropostas';
import { FormTarefa, rascunhoInicial } from './FormTarefa';
import { useFunis } from './Funis';

/**
 * CRM no novo desenho, lote 3 (C-A, ficha 2026-10-26 "crm-novo-desenho"): a página Comercial do CRM (o funil e a lista do clone
 * do RD) sobre as oportunidades de sempre. Funil e responsáveis, etapa (na lista), status com "Pausada", ordenação (14), drawer
 * de filtros com os filtros salvos, as etiquetas dos filtros ligados, estatísticas por etapa, personalizar cartões, e as ações
 * em massa da lista. A ficha da negociação (lote 4) abre em `/crm/comercial/negociacao/:id`.
 */

const VISAO = 'pipeline.crm.visao';
/** Exportação: páginas de 200 até o total do filtro (até 1.000), num laço por página. */
const POR_PAGINA = 200;
const lerVisao = (): 'funil' | 'lista' => {
  try {
    return localStorage.getItem(VISAO) === 'lista' ? 'lista' : 'funil';
  } catch {
    return 'funil';
  }
};
export const CAMPOS_CARTAO = ['empresa', 'qualificacao', 'responsavel', 'valor', 'proxima_tarefa', 'criada_em', 'fonte', 'previsao', 'vertical', 'regime'] as const;

export function PaginaComercial() {
  return (
    <Routes>
      <Route index element={<Comercial />} />
      <Route path="negociacao/:id/proposta" element={<NovaProposta />} />
      <Route path="negociacao/:id" element={<FichaComFeedback />} />
      <Route path="*" element={<Comercial />} />
    </Routes>
  );
}

function Comercial() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const funis = useFunis();
  const ativos = (funis.data?.itens ?? []).filter((f) => !f.arquivado_em);
  // C17: "Ver relatório completo" das Atividades chega com o funil e as estatísticas abertas no endereço.
  const pedido = lerPedidoDoRelatorio(useLocation().search);
  // Lista de 03/11, item 6: sem funil no endereço, o último escolhido por esta pessoa neste workspace.
  const [funilId, setFunilId] = useState(() => pedido.funil || lerFunilLembrado(s.workspace.id, s.usuario.id));
  const funil = funilDoPedido(funilId, ativos);
  const [visao, setVisao] = useState<'funil' | 'lista'>(() => (pedido.estatisticas ? 'funil' : lerVisao()));
  const [f, setF] = useState<FiltrosComercial>(() => (pedido.semTarefa ? { ...filtrosVazios(), sem_tarefa: true } : filtrosVazios()));
  const [ordem, setOrdem] = useState<Ordem>('criadas_ultimo');
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(25);
  const [filtros, setFiltros] = useState(false);
  const [salvoId, setSalvoId] = useState('');
  const [mostrar, setMostrar] = useState<Record<string, boolean>>({});
  const [cartoes, setCartoes] = useState(false);
  const [criar, setCriar] = useState<'negociacao' | 'tarefa' | null>(null);
  const [chaveFunil, setChaveFunil] = useState(0);
  const [sobre, setSobre] = useState<NegociacaoCrm | null>(null);
  const feedback = useFeedback();
  const catalogo = useEmpresasDoCatalogo();
  // Ajustes 27/10 (K2): empresa e contato do menu "Criar" são do Cadastros (publicados em `criacoes`), com o módulo e a permissão.
  const criacao = useAbrirCriacao();
  const criarEmpresa = useCriacaoDaChave('empresa');
  const criarContato = useCriacaoDaChave('contato');
  // C6: o botão flutuante cria a negociação já no funil escolhido aqui.
  useContextoDaCriacao({ funilId: funil || undefined });
  useEffect(() => {
    try {
      localStorage.setItem(VISAO, visao);
    } catch {
      // Sem armazenamento (janela privada): a escolha vale só nesta visita.
    }
  }, [visao]);
  const params = useMemo(() => paramsDosFiltros(f, funil || undefined), [f, funil]);
  const membros = useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros'), staleTime: 300_000 });
  const quadro = useQuery({
    queryKey: ['pipeline', 'crm', 'funil', params, ordem],
    queryFn: () => get<{ funil_id: string; etapas: EtapaCrm[] }>('/pipeline/crm/funil', { ...params, ordem }),
    enabled: !!funil,
    placeholderData: keepPreviousData,
  });
  const lista = useQuery({
    queryKey: ['pipeline', 'crm', 'lista', params, ordem, pagina, tamanho],
    queryFn: () => get<{ total: number; itens: NegociacaoCrm[] }>('/pipeline/crm/negociacoes', { ...params, ordem, pagina, limite: tamanho }),
    enabled: !!funil && visao === 'lista',
    placeholderData: keepPreviousData,
  });
  const algumaAberta = Object.values(mostrar).some(Boolean);
  const est = useQuery({
    queryKey: ['pipeline', 'crm', 'estatisticas', params],
    queryFn: () => get<{ itens: EstatisticaEtapa[] }>('/pipeline/crm/estatisticas', params),
    enabled: !!funil && visao === 'funil' && algumaAberta,
  });
  const campos = useQuery({ queryKey: ['pipeline', 'crm', 'cartoes'], queryFn: () => get<{ campos: string[] }>('/pipeline/crm/cartoes') });
  const etapas = useMemo(() => quadro.data?.etapas ?? [], [quadro.data]);
  const [relatorioAberto, setRelatorioAberto] = useState(false);
  useEffect(() => {
    if (!pedido.estatisticas || relatorioAberto || !etapas.length) return;
    setRelatorioAberto(true);
    setMostrar(Object.fromEntries(etapas.map((e) => [e.id, true])));
  }, [pedido.estatisticas, relatorioAberto, etapas]);
  const total = visao === 'lista' ? (lista.data?.total ?? 0) : etapas.reduce((a, e) => a + e.total, 0);
  const mudarFiltros = (novo: FiltrosComercial) => {
    setF(novo);
    setPagina(1);
  };
  const mover = useMutation({
    mutationFn: (v: { id: string; etapa_id: string }) => post(`/pipeline/oportunidades/${v.id}/mover`, { etapa_id: v.etapa_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline'] }),
    onError: async (e) => {
      toast.erro(mensagemDeErro(e));
      setChaveFunil((k) => k + 1);
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
    },
  });
  const exportar = async () => {
    try {
      const linhas: NegociacaoCrm[] = [];
      for (let p = 1; p <= Math.max(1, Math.ceil(Math.min(total, 1000) / 200)); p++) linhas.push(...(await get<{ itens: NegociacaoCrm[] }>('/pipeline/crm/negociacoes', { ...params, ordem, pagina: p, limite: POR_PAGINA })).itens);
      const c = t('crm.comercial.exportCab', { returnObjects: true }) as string[];
      baixarTexto(t('crm.comercial.arquivoCsv'), csv([c, ...linhas.map((x) => [x.titulo, x.parceiro_nome, rotuloEtapaPipeline(x.etapa_nome), t(`crm.comercial.statusNeg.${x.status}`), x.responsavel_nome, x.qualificacao, x.utm_fonte, x.utm_campanha, dinheiro(x.valor_unico_centavos, x.moeda), dinheiro(x.valor_recorrente_centavos, x.moeda), dataHora(x.created_at), x.previsao_fechamento])]));
      toast.sucesso(t('crm.comercial.exportadas', { count: linhas.length }));
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  };

  // Etiquetas dos filtros ligados (com o "x" que tira cada um), como no RD.
  const nomeMembro = (id: string) => nomePessoa(membros.data?.itens.find((m) => m.id === id)?.nome ?? '');
  const etiquetas: { chave: string; texto: string; tirar: () => void }[] = [];
  if (f.responsaveis.length) etiquetas.push({ chave: 'resp', texto: f.responsaveis.length === 1 ? nomeMembro(f.responsaveis[0]!) : t('crm.atividades.nResponsaveis', { count: f.responsaveis.length }), tirar: () => mudarFiltros({ ...f, responsaveis: [] }) });
  if (f.status !== 'todos') etiquetas.push({ chave: 'status', texto: t(`crm.comercial.statusFiltro.${f.status}`), tirar: () => mudarFiltros({ ...f, status: 'todos' }) });
  if (f.predefinido) etiquetas.push({ chave: 'pre', texto: t(`crm.filtros.predef.${f.predefinido}`), tirar: () => mudarFiltros({ ...f, predefinido: '' }) });
  if (f.sem_tarefa) etiquetas.push({ chave: 'semtarefa', texto: t('crm.filtros.semTarefa'), tirar: () => mudarFiltros({ ...f, sem_tarefa: false }) });
  if (f.nome.trim()) etiquetas.push({ chave: 'nome', texto: `${t('crm.filtros.nome')}: ${f.nome}`, tirar: () => mudarFiltros({ ...f, nome: '' }) });
  if (f.qualificacao) etiquetas.push({ chave: 'qualif', texto: t('crm.comercial.estrelas', { count: Number(f.qualificacao) }), tirar: () => mudarFiltros({ ...f, qualificacao: '' }) });
  if (f.parceiro_id) etiquetas.push({ chave: 'empresa', texto: `${t('crm.filtros.empresa')}: ${f.parceiro_nome}`, tirar: () => mudarFiltros({ ...f, parceiro_id: '', parceiro_nome: '' }) });
  if (f.campanha.trim()) etiquetas.push({ chave: 'campanha', texto: `${t('crm.filtros.campanha')}: ${f.campanha}`, tirar: () => mudarFiltros({ ...f, campanha: '' }) });
  if (f.fonte.trim()) etiquetas.push({ chave: 'fonte', texto: `${t('crm.filtros.fonte')}: ${f.fonte}`, tirar: () => mudarFiltros({ ...f, fonte: '' }) });
  if (f.servico_id) etiquetas.push({ chave: 'produto', texto: `${t('crm.filtros.produto')}: ${f.servico_nome}`, tirar: () => mudarFiltros({ ...f, servico_id: '', servico_nome: '' }) });
  if (f.vertical) {
    const e = catalogo.data?.empresas.find((x) => x.id === f.vertical);
    etiquetas.push({ chave: 'vertical', texto: `${t('crm.filtros.vertical')}: ${e ? nomeDaEmpresa(e) : ''}`, tirar: () => mudarFiltros({ ...f, vertical: '' }) });
  }
  for (const k of ['criada', 'contato', 'proxima', 'fechada', 'previsao'] as const) {
    if (f[k]) etiquetas.push({ chave: k, texto: `${t(`crm.filtros.datas.${k}`)}: ${t(`crm.filtros.presets.${f[k]}`)}`, tirar: () => mudarFiltros({ ...f, [k]: '' }) });
  }
  for (const v of f.vazios) etiquetas.push({ chave: `vazio-${v}`, texto: t('crm.filtros.vazioEtiqueta', { campo: t(`crm.filtros.campoVazio.${v}`) }), tirar: () => mudarFiltros({ ...f, vazios: f.vazios.filter((x) => x !== v) }) });
  if (f.valor_min.trim() || f.valor_max.trim()) etiquetas.push({ chave: 'valor', texto: t('crm.filtros.valorEtiqueta', { de: f.valor_min || '0', ate: f.valor_max || '∞' }), tirar: () => mudarFiltros({ ...f, valor_min: '', valor_max: '' }) });
  if (f.estatistica) etiquetas.push({ chave: 'est', texto: t('crm.comercial.estatisticaEtiqueta', { nome: t(`crm.comercial.estatisticas.${f.estatistica.chave}`) }), tirar: () => mudarFiltros({ ...f, estatistica: null }) });

  const nResp = f.responsaveis.length;
  const resumoResp = !nResp ? t('crm.comercial.todasNegociacoes') : nResp === 1 && f.responsaveis[0] === s.usuario.id ? t('crm.comercial.minhasNegociacoes') : nResp === 1 ? nomeMembro(f.responsaveis[0]!) : t('crm.atividades.nResponsaveis', { count: nResp });
  const podeEditar = s.pode('pipeline.editar');
  // K11: reordenar as etapas pelo funil (vale para todos), só com permissão de editar o funil; se falhar, remonta.
  const reordenar = async (ids: string[]) => {
    try {
      await post('/pipeline/etapas/reordenar', { funil_id: funil, ids });
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
    } catch (e) {
      toast.erro(mensagemDeErro(e));
      setChaveFunil((k) => k + 1);
    }
  };
  const recarregar = async (aviso: string) => {
    await qc.invalidateQueries({ queryKey: ['pipeline', 'crm'] });
    toast.sucesso(aviso);
  };
  const icone = (I: typeof Plus) => <I className="size-4" aria-hidden />;

  return (
    <div className="flex min-w-0 flex-col gap-4" data-pagina-comercial="">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="kicker">{t('casca.comercial')}</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">{t('crm.comercial.titulo')}</h1>
          <p className="mt-1 text-sm text-tinta-3">{t('crm.comercial.subtitulo')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-borda p-0.5" role="group" aria-label={t('crm.comercial.visao')}>
            {(['funil', 'lista'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={visao === v}
                data-visao={v}
                onClick={() => {
                  setVisao(v);
                  setPagina(1);
                }}
                aria-label={t(`crm.comercial.visoes.${v}`)}
                title={t(`crm.comercial.visoes.${v}`)}
                className="flex size-9 items-center justify-center rounded-md text-tinta-2 aria-pressed:bg-sup-3 aria-pressed:text-tinta-1"
              >
                {v === 'funil' ? <KanbanSquare className="size-4" aria-hidden /> : <List className="size-4" aria-hidden />}
              </button>
            ))}
          </div>
          {/* K5: Exportar, Recarregar, Configurar funil (K14: a configuração completa), Personalizar cartões e Dê seu feedback. */}
          <Menu
            rotulo={t('crm.comercial.maisOpcoes')}
            classeGatilho={classesBotao('secundario', 'md')}
            gatilho={<MoreHorizontal className="size-4" aria-hidden />}
            itens={[
              // Val5 (29/10): exportar tem ação própria (`pipeline.exportar`); funis, cartões e a ordem das etapas, `pipeline.configurar`.
              ...(s.pode('pipeline.exportar') ? [{ chave: 'exportar', rotulo: t('crm.atividades.exportar'), icone: icone(Download), aoClicar: () => void exportar() }] : []),
              { chave: 'recarregar', rotulo: t('crm.comercial.recarregar'), icone: icone(RefreshCw), aoClicar: () => void recarregar(t('crm.comercial.recarregado')) },
              ...(podeNoWorkspace(s, 'pipeline.configurar') ? [{ chave: 'funis', rotulo: t('crm.comercial.configurarFunil'), icone: icone(Settings), aoClicar: () => navegar(`${CAMINHO_CONFIGURACOES}/funis`) }] : []),
              ...(visao === 'funil' && podeNoWorkspace(s, 'pipeline.configurar') ? [{ chave: 'cartoes', rotulo: t('crm.comercial.personalizarCartoes'), icone: icone(Pencil), aoClicar: () => setCartoes(true) }] : []),
              { chave: 'feedback', rotulo: tn('feedback.menu'), icone: ICONE_FEEDBACK, aoClicar: feedback.abrir },
            ]}
          />
          {/* K3: atalho de tarefas entre o Mais opções e as estatísticas (ordem da origem). */}
          <BotaoAtalhoTarefas />
          {/* K4: as estatísticas das etapas só com o ícone (o nome no title e no aria-label). */}
          {visao === 'funil' && (
            <button
              type="button"
              aria-pressed={algumaAberta}
              aria-label={t('crm.comercial.estatisticasEtapas')}
              title={t('crm.comercial.estatisticasEtapas')}
              data-todas-estatisticas=""
              onClick={() => setMostrar(Object.fromEntries(etapas.map((e) => [e.id, !algumaAberta])))}
              className={`${classesBotao('secundario', 'md')} aria-pressed:border-acento-forte aria-pressed:text-acento-forte`}
            >
              <BarChart3 className="size-4" aria-hidden />
            </button>
          )}
          {/* K2: Negociação, Empresa, Contato e Tarefa, com os ícones (empresa e contato só com o Cadastros e a permissão). */}
          {(s.pode('pipeline.criar') || criarEmpresa || criarContato) && (
            <Menu
              rotulo={t('crm.comercial.criar')}
              classeGatilho={classesBotao('primario', 'md')}
              gatilho={
                <>
                  <Plus className="size-4" aria-hidden />
                  {t('crm.comercial.criar')}
                  <ChevronDown className="size-4" aria-hidden />
                </>
              }
              itens={[
                ...(s.pode('pipeline.criar') ? [{ chave: 'negociacao', rotulo: t('crm.comercial.criarNegociacao'), icone: icone(Handshake), aoClicar: () => setCriar('negociacao') }] : []),
                ...(criarEmpresa ? [{ chave: 'empresa', rotulo: tn('criacao.itens.empresa'), icone: icone(Building2), aoClicar: () => criacao.abrir(criarEmpresa) }] : []),
                ...(criarContato ? [{ chave: 'contato', rotulo: tn('criacao.itens.contato'), icone: icone(Contact), aoClicar: () => criacao.abrir(criarContato) }] : []),
                ...(s.pode('pipeline.criar') ? [{ chave: 'tarefa', rotulo: t('crm.atividades.criar'), icone: icone(ListTodo), aoClicar: () => setCriar('tarefa') }] : []),
              ]}
            />
          )}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2" data-filtros-comercial="">
        <div className="w-full sm:w-56">
          <Selecao
            aria-label={t('funis.funil')}
            icone={<Filter className="size-4" />}
            data-funil=""
            value={funil}
            opcoes={ativos.map((x) => ({ valor: x.id, rotulo: rotuloFunilPipeline(x.nome) }))}
            onChange={(e) => {
              setFunilId(e.target.value);
              lembrarFunil(s.workspace.id, s.usuario.id, e.target.value);
              mudarFiltros({ ...f, etapa_id: '', estatistica: null });
            }}
          />
        </div>
        <div className="w-full sm:w-60">
          <FiltroMultiplo
            data-filtro-responsaveis=""
            rotulo={t('crm.atividades.responsaveis')}
            resumo={resumoResp}
            icone={<UserRound className="size-4 shrink-0 text-tinta-3" aria-hidden />}
            opcoes={(membros.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))}
            marcados={f.responsaveis}
            aoMudar={(v) => mudarFiltros({ ...f, responsaveis: v })}
            limpar={t('crm.atividades.limparFiltros')}
            extra={
              <button type="button" className="rounded px-2 py-1 text-left text-sm font-medium text-acento-forte hover:bg-sup-3" onClick={() => mudarFiltros({ ...f, responsaveis: [s.usuario.id] })}>
                {t('crm.comercial.minhasNegociacoes')}
              </button>
            }
          />
        </div>
        {visao === 'lista' && (
          <div className="w-full sm:w-52">
            <Selecao aria-label={t('crm.comercial.etapa')} icone={<BarChart3 className="size-4" />} value={f.etapa_id} vazio={t('crm.comercial.todasEtapas')} opcoes={etapas.map((e) => ({ valor: e.id, rotulo: rotuloEtapaPipeline(e.nome) }))} onChange={(e) => mudarFiltros({ ...f, etapa_id: e.target.value })} />
          </div>
        )}
        <div className="w-full sm:w-52">
          <Selecao aria-label={t('crm.comercial.status')} icone={<CircleDot className="size-4" />} data-filtro-status="" value={f.status} opcoes={STATUS.map((x) => ({ valor: x, rotulo: t(`crm.comercial.statusFiltro.${x}`) }))} onChange={(e) => mudarFiltros({ ...f, status: e.target.value as FiltrosComercial['status'] })} />
        </div>
        <div className="w-full sm:w-56">
          <Selecao aria-label={t('crm.comercial.ordenacao')} icone={<ArrowDownUp className="size-4" />} data-ordem="" value={ordem} opcoes={ORDENS.map((x) => ({ valor: x, rotulo: t(`crm.comercial.ordens.${x}`) }))} onChange={(e) => setOrdem(e.target.value as Ordem)} />
        </div>
        <Botao icone={<Filter className="size-4" />} onClick={() => setFiltros(true)} data-abrir-filtros="">
          {t('crm.comercial.filtrosN', { n: contarFiltros(f) })}
        </Botao>
      </div>

      <ul className="flex flex-wrap items-center gap-2" aria-label={t('crm.comercial.filtrosLigados')} data-etiquetas="">
        <li className="rounded-full bg-sup-3 px-3 py-1 text-xs font-semibold" data-total-negociacoes="">
          {t('crm.comercial.nNegociacoes', { count: total })}
        </li>
        {etiquetas.map((x) => (
          <li key={x.chave} className="flex items-center gap-1 rounded-full border border-borda bg-sup-1 py-0.5 pr-1 pl-3 text-xs">
            {x.texto}
            <button type="button" className="rounded-full p-1 hover:bg-sup-3" aria-label={t('crm.comercial.tirarFiltro', { filtro: x.texto })} onClick={x.tirar}>
              <X className="size-3" aria-hidden />
            </button>
          </li>
        ))}
      </ul>

      {visao === 'funil' ? (
        <FunilComercial
          key={`${funil}-${chaveFunil}`}
          etapas={etapas}
          estatisticas={est.data?.itens ?? []}
          campos={campos.data?.campos ?? []}
          mostrar={mostrar}
          aoMostrar={(id, v) => setMostrar((m) => ({ ...m, [id]: v }))}
          estatistica={f.estatistica}
          aoFiltrarEstatistica={(v) => mudarFiltros({ ...f, estatistica: v })}
          aoMover={(n, etapa) => {
            const destino = etapas.find((e) => e.id === etapa);
            // Lista de 30/10 (B), item 8: a pausada não anda de etapa antes de ser retomada.
            if (n.status === 'pausado') {
              toast.erro(t('crm.pausa.naoMove'));
              setChaveFunil((k) => k + 1);
              return;
            }
            if (destino?.tipo !== 'aberta') {
              toast.erro(t('crm.comercial.moverSoAbertas'));
              setChaveFunil((k) => k + 1);
              return;
            }
            mover.mutate({ id: n.id, etapa_id: etapa });
          }}
          aoAbrir={(n) => navegar(caminhoDaNegociacao(n.id))}
          podeMover={podeEditar}
          aoRecarregar={() => void recarregar(t('crm.comercial.etapaRecarregada'))}
          aoReordenar={podeNoWorkspace(s, 'pipeline.configurar') ? (ids) => void reordenar(ids) : undefined}
          aoSobre={setSobre}
          catalogo={catalogo.data?.empresas ?? []}
        />
      ) : (
        <ListaComercial
          total={lista.data?.total ?? 0}
          itens={lista.data?.itens ?? []}
          pagina={pagina}
          tamanho={tamanho}
          aoPagina={setPagina}
          aoTamanho={(n) => {
            setTamanho(n);
            setPagina(1);
          }}
          etapas={etapas}
          paramsFiltro={params}
          carregando={lista.isFetching}
          aoSobre={setSobre}
        />
      )}

      {filtros && (
        <DrawerFiltros
          atual={f}
          salvoAtual={salvoId}
          aoAplicar={(novo, salvo) => {
            mudarFiltros(novo);
            setSalvoId(salvo);
          }}
          aoFechar={() => setFiltros(false)}
        />
      )}
      {cartoes && <PersonalizarCartoes atuais={campos.data?.campos ?? []} aoFechar={() => setCartoes(false)} />}
      {criar === 'negociacao' && <CriarNegociacaoCrm aoFechar={() => setCriar(null)} contexto={{ funilId: funil }} />}
      {criar === 'tarefa' && <FormTarefa inicial={rascunhoInicial(s.usuario.id)} aoFechar={() => setCriar(null)} />}
      <DialogoCriacao aberta={criacao.aberta} aoFechar={criacao.fechar} />
      {sobre && <SobreNegociacao n={sobre} aoFechar={() => setSobre(null)} />}
      {feedback.dialogo}
    </div>
  );
}

/** "Personalizar cartões": os campos que aparecem no cartão do funil (vale para o workspace). */
function PersonalizarCartoes({ atuais, aoFechar }: { atuais: string[]; aoFechar: () => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [campos, setCampos] = useState(atuais);
  const salvar = useMutation({
    mutationFn: () => api('PUT', '/pipeline/crm/cartoes', { campos }),
    onSuccess: async () => {
      toast.sucesso(t('crm.comercial.cartoesSalvos'));
      await qc.invalidateQueries({ queryKey: ['pipeline', 'crm', 'cartoes'] });
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.comercial.personalizarCartoes')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={salvar.isPending} onClick={() => salvar.mutate()} data-salvar-cartoes="">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <p className="mb-3 text-sm text-tinta-2">{t('crm.comercial.cartoesAjuda')}</p>
      <ul className="flex flex-col" data-campos-cartao="">
        {CAMPOS_CARTAO.map((c) => (
          <li key={c} className="border-b border-borda">
            <label className="flex min-h-11 items-center gap-3 text-sm font-medium">
              <input type="checkbox" value={c} checked={campos.includes(c)} onChange={(e) => setCampos((x) => (e.target.checked ? [...x, c] : x.filter((y) => y !== c)))} />
              {t(`crm.comercial.camposCartao.${c}`)}
            </label>
          </li>
        ))}
      </ul>
    </Drawer>
  );
}

/** F11 (ligação das trilhas): o "Dê seu feedback" no Mais opções da ficha da negociação (decisão 5). */
function FichaComFeedback() {
  const { t: tn } = useTranslation();
  const feedback = useFeedback();
  return (
    <>
      <FichaNegociacao itensMenu={[{ chave: 'feedback', rotulo: tn('feedback.menu'), icone: ICONE_FEEDBACK, aoClicar: feedback.abrir }]} />
      {feedback.dialogo}
    </>
  );
}
